/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { window, workspace, Uri, Disposable, Event, EventEmitter, FileDecoration, FileDecorationProvider, ThemeColor } from 'vscode';
import * as path from 'path';
import { $Smc, GitResourceGroup } from './repository';
import { $Gmc } from './model';
import { $jmc } from './decorators';
import { $Vlc, $Plc, $Wlc, $Tlc, $emc } from './util';
import { GitErrorCodes, Status } from './api/git';

class GitIgnoreDecorationProvider implements FileDecorationProvider {

	private static a: FileDecoration = { color: new ThemeColor('gitDecoration.ignoredResourceForeground') };

	readonly onDidChangeFileDecorations: Event<Uri[]>;
	private b = new Map<string, { repository: $Smc; queue: Map<string, $emc<FileDecoration | undefined>> }>();
	private c: Disposable[] = [];

	constructor(private f: $Gmc) {
		this.onDidChangeFileDecorations = $Tlc($Wlc<any>(
			$Vlc(workspace.onDidSaveTextDocument, e => /\.gitignore$|\.git\/info\/exclude$/.test(e.uri.path)),
			f.onDidOpenRepository,
			f.onDidCloseRepository
		));

		this.c.push(window.registerFileDecorationProvider(this));
	}

	async provideFileDecoration(uri: Uri): Promise<FileDecoration | undefined> {
		const repository = this.f.getRepository(uri);

		if (!repository) {
			return;
		}

		let queueItem = this.b.get(repository.root);

		if (!queueItem) {
			queueItem = { repository, queue: new Map<string, $emc<FileDecoration | undefined>>() };
			this.b.set(repository.root, queueItem);
		}

		let promiseSource = queueItem.queue.get(uri.fsPath);

		if (!promiseSource) {
			promiseSource = new $emc();
			queueItem!.queue.set(uri.fsPath, promiseSource);
			this.g();
		}

		return await promiseSource.promise;
	}

	@$jmc(500)
	private g(): void {
		const queue = new Map(this.b.entries());
		this.b.clear();

		for (const [, item] of queue) {
			const paths = [...item.queue.keys()];

			item.repository.checkIgnore(paths).then(ignoreSet => {
				for (const [path, promiseSource] of item.queue.entries()) {
					promiseSource.resolve(ignoreSet.has(path) ? GitIgnoreDecorationProvider.a : undefined);
				}
			}, err => {
				if (err.gitErrorCode !== GitErrorCodes.IsInSubmodule) {
					console.error(err);
				}

				for (const [, promiseSource] of item.queue.entries()) {
					promiseSource.reject(err);
				}
			});
		}
	}

	dispose(): void {
		this.c.forEach(d => d.dispose());
		this.b.clear();
	}
}

class GitDecorationProvider implements FileDecorationProvider {

	private static a: FileDecoration = {
		tooltip: 'Submodule',
		badge: 'S',
		color: new ThemeColor('gitDecoration.submoduleResourceForeground')
	};

	private readonly b = new EventEmitter<Uri[]>();
	readonly onDidChangeFileDecorations: Event<Uri[]> = this.b.event;

	private c: Disposable[] = [];
	private f = new Map<string, FileDecoration>();

	constructor(private g: $Smc) {
		this.c.push(
			window.registerFileDecorationProvider(this),
			g.onDidRunGitStatus(this.h, this)
		);
	}

	private h(): void {
		const newDecorations = new Map<string, FileDecoration>();

		this.j(newDecorations);
		this.i(this.g.indexGroup, newDecorations);
		this.i(this.g.untrackedGroup, newDecorations);
		this.i(this.g.workingTreeGroup, newDecorations);
		this.i(this.g.mergeGroup, newDecorations);

		const uris = new Set([...this.f.keys()].concat([...newDecorations.keys()]));
		this.f = newDecorations;
		this.b.fire([...uris.values()].map(value => Uri.parse(value, true)));
	}

	private i(group: GitResourceGroup, bucket: Map<string, FileDecoration>): void {
		for (const r of group.resourceStates) {
			const decoration = r.resourceDecoration;

			if (decoration) {
				// not deleted and has a decoration
				bucket.set(r.original.toString(), decoration);

				if (r.type === Status.DELETED && r.rightUri) {
					bucket.set(r.rightUri.toString(), decoration);
				}

				if (r.type === Status.INDEX_RENAMED || r.type === Status.INTENT_TO_RENAME) {
					bucket.set(r.resourceUri.toString(), decoration);
				}
			}
		}
	}

	private j(bucket: Map<string, FileDecoration>): void {
		for (const submodule of this.g.submodules) {
			bucket.set(Uri.file(path.join(this.g.root, submodule.path)).toString(), GitDecorationProvider.a);
		}
	}

	provideFileDecoration(uri: Uri): FileDecoration | undefined {
		return this.f.get(uri.toString());
	}

	dispose(): void {
		this.c.forEach(d => d.dispose());
	}
}


export class $4mc {

	private a: Disposable[] = [];
	private b: Disposable[] = [];
	private c = new Map<$Smc, Disposable>();

	constructor(private f: $Gmc) {
		this.a.push(new GitIgnoreDecorationProvider(f));

		const onEnablementChange = $Vlc(workspace.onDidChangeConfiguration, e => e.affectsConfiguration('git.decorations.enabled'));
		onEnablementChange(this.g, this, this.a);
		this.g();
	}

	private g(): void {
		const enabled = workspace.getConfiguration('git').get('decorations.enabled');

		if (enabled) {
			this.h();
		} else {
			this.i();
		}
	}

	private h(): void {
		this.f.onDidOpenRepository(this.j, this, this.b);
		this.f.onDidCloseRepository(this.k, this, this.b);
		this.f.repositories.forEach(this.j, this);
	}

	private i(): void {
		this.b = $Plc(this.b);
		this.c.forEach(value => value.dispose());
		this.c.clear();
	}

	private j(repository: $Smc): void {
		const provider = new GitDecorationProvider(repository);
		this.c.set(repository, provider);
	}

	private k(repository: $Smc): void {
		const provider = this.c.get(repository);

		if (provider) {
			provider.dispose();
			this.c.delete(repository);
		}
	}

	dispose(): void {
		this.i();
		this.a = $Plc(this.a);
	}
}
